/**
 * EmailStatusPage — iter866
 *
 * Admin view of the effective email delivery chain. Shows which
 * provider will handle the next scheduled report / digest, exposes the
 * SMTP circuit-breaker state, and provides a one-click test send.
 */
import { useEffect, useState } from 'react';
import { Mail, Cloud, Server, AlertTriangle, CheckCircle2, XCircle, Send } from 'lucide-react';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Select, SelectTrigger, SelectContent, SelectItem, SelectValue } from '@/components/ui/select';
import { toast } from 'sonner';
import api from '@/lib/api';

export default function EmailStatusPage() {
  const [status, setStatus] = useState(null);
  const [loading, setLoading] = useState(true);
  const [testing, setTesting] = useState(false);
  const [to, setTo] = useState('');
  const [prov, setProv] = useState('auto');

  const load = async () => {
    try {
      const res = await api.get('/admin/email/status');
      setStatus(res.data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Failed to load email status');
    } finally { setLoading(false); }
  };

  useEffect(() => { load(); }, []);

  const runTest = async () => {
    if (!to) { toast.error('Enter a destination email'); return; }
    setTesting(true);
    try {
      const res = await api.post('/admin/email/test', { to_email: to, provider: prov === 'auto' ? null : prov });
      if (res.data.delivered) {
        toast.success(`Delivered via ${prov === 'auto' ? status?.effective_provider : prov} in ${res.data.elapsed_ms}ms`);
      } else {
        toast.error(`Delivery failed${res.data.error ? ` — ${res.data.error}` : ''}`);
      }
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Test send crashed');
    } finally { setTesting(false); }
  };

  if (loading) return <div className="p-8 text-muted-foreground" data-testid="email-status-loading">Loading email provider status…</div>;

  const effective = status?.effective_provider;
  const bo = status?.smtp?.circuit_breaker?.open;

  return (
    <div className="max-w-5xl mx-auto p-6 space-y-6" data-testid="email-status-page">
      <div>
        <h1 className="text-3xl font-bold flex items-center gap-2"><Mail size={26} className="text-primary" />Email Delivery</h1>
        <p className="text-sm text-muted-foreground mt-1">Scheduled reports, daily digests, and WhatsApp fallback emails route through this chain. SMTP is tried first — if it fails, Resend takes over automatically.</p>
      </div>

      {/* Effective provider hero */}
      <Card className="p-5" data-testid="email-effective-card">
        <div className="flex items-center gap-3">
          {effective === 'none' ? <XCircle className="text-error" size={28} /> : <CheckCircle2 className="text-emerald-600" size={28} />}
          <div className="flex-1">
            <p className="text-[11px] uppercase tracking-wider text-muted-foreground">Next email will be sent via</p>
            <p className="text-2xl font-bold capitalize" data-testid="email-effective-provider">{effective || '—'}</p>
          </div>
          {bo && <Badge variant="destructive" className="animate-pulse">SMTP breaker open</Badge>}
        </div>
      </Card>

      {/* Providers */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        {/* SMTP */}
        <Card className="p-4">
          <div className="flex items-center gap-2 mb-2"><Server size={18} className="text-indigo-600" /><span className="font-semibold">SMTP</span>
            {status?.smtp?.configured ? <Badge className="bg-emerald-100 text-emerald-800 border-emerald-200">Configured</Badge> : <Badge variant="outline">Not configured</Badge>}
          </div>
          <div className="space-y-1 text-xs">
            <div className="flex justify-between"><span className="text-muted-foreground">Host</span><span className="font-mono">{status?.smtp?.host || '—'}</span></div>
            <div className="flex justify-between"><span className="text-muted-foreground">Mode</span><span className="font-mono">{status?.smtp?.mode}</span></div>
            <div className="flex justify-between"><span className="text-muted-foreground">From</span><span className="font-mono truncate max-w-[220px]" title={status?.smtp?.from_email}>{status?.smtp?.from_email || '—'}</span></div>
            {bo && (
              <div className="flex items-center gap-1 mt-2 p-2 bg-warning/10 rounded" data-testid="smtp-breaker-open">
                <AlertTriangle size={14} className="text-warning" />
                <span className="text-warning">Circuit open — {status.smtp.circuit_breaker.seconds_remaining}s left. Fix M365 creds &amp; test to reset.</span>
              </div>
            )}
          </div>
        </Card>
        {/* Resend */}
        <Card className="p-4">
          <div className="flex items-center gap-2 mb-2"><Cloud size={18} className="text-fuchsia-600" /><span className="font-semibold">Resend</span>
            {status?.resend?.configured ? <Badge className="bg-emerald-100 text-emerald-800 border-emerald-200">Configured</Badge> : <Badge variant="outline">Not configured</Badge>}
          </div>
          <div className="space-y-1 text-xs">
            <div className="flex justify-between"><span className="text-muted-foreground">API key</span><span className="font-mono">{status?.resend?.api_key_last4 ? `••••${status.resend.api_key_last4}` : '—'}</span></div>
            <div className="flex justify-between"><span className="text-muted-foreground">From</span><span className="font-mono truncate max-w-[220px]" title={status?.resend?.from_email}>{status?.resend?.from_email || 'Smarto <alerts@smartoerp.com> (default)'}</span></div>
            {!status?.resend?.configured && (
              <div className="mt-2 p-2 bg-secondary/50 rounded text-[11px] leading-relaxed" data-testid="resend-setup-hint">
                Set <code className="bg-white px-1">RESEND_API_KEY</code> and (optionally) <code className="bg-white px-1">RESEND_FROM_EMAIL</code> in <code>backend/.env</code> — grab a key at <a className="text-primary underline" href="https://resend.com/api-keys" target="_blank" rel="noreferrer">resend.com/api-keys</a>. Free plan = 3,000 emails/month.
              </div>
            )}
          </div>
        </Card>
      </div>

      {/* Test send */}
      <Card className="p-4" data-testid="email-test-card">
        <p className="text-sm font-semibold mb-3 flex items-center gap-2"><Send size={16} />Send a test email</p>
        <div className="flex flex-col md:flex-row gap-3 items-end">
          <div className="flex-1">
            <Label className="text-xs">Destination email</Label>
            <Input type="email" placeholder="you@example.com" value={to} onChange={(e) => setTo(e.target.value)} data-testid="email-test-to" />
          </div>
          <div>
            <Label className="text-xs">Provider</Label>
            <Select value={prov} onValueChange={setProv}>
              <SelectTrigger className="w-40" data-testid="email-test-provider"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="auto">Auto (default)</SelectItem>
                <SelectItem value="smtp">SMTP only</SelectItem>
                <SelectItem value="resend">Resend only</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <Button onClick={runTest} disabled={testing || !to} data-testid="email-test-send">
            {testing ? 'Sending…' : 'Send test'}
          </Button>
        </div>
        <p className="text-[10px] text-muted-foreground mt-2">Uses the same helper (<code>utils/email_service.send_email</code>) that scheduled reports call.</p>
      </Card>
    </div>
  );
}
